import { ApeironBlueButton, MayNull, useAccountContext } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import ApostleBountiesDataGrid from '@src/components/apostles/bounty/ApostleBountiesDataGrid'
import ApostleBountyFilter from '@src/components/apostles/bounty/ApostleBountyFilter'
import ClaimApostleBountyDialog from '@src/components/apostles/bounty/ClaimApostleBountyDialog'
import CreateApostleBountyDialog from '@src/components/apostles/bounty/CreateApostleBountyDialog'
import ListTemplate from '@src/components/layout/ListTemplate'
import BountyHeader from '@src/components/share/bounty/BountyHeader'
import DuplicateSVG from '@src/components/share/icon/DuplicateSVG'
import { NftType } from '@src/constants/bookmark'
import {
  BountyProvider,
  useBountyContext
} from '@src/contexts/share/BountyContext'
import { BountyFilterInput, BountyNode } from '@src/types/bounty'
import * as R from 'ramda'
import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { formatEther } from 'viem'

const ClaimButton = styled(ApeironBlueButton)`
  :disabled {
    background-color: #27282a;
  }
  border-radius: 4px;
  width: 130px;
`

const DuplicateButton = styled(ApeironBlueButton)`
  width: 130px;
`

const Container = styled(Box)`
  ${props => props.theme.breakpoints.down('res768')} {
    padding: 20px 2px;
  }
`

const ApostleBountyContent: FC<ContentProps> = props => {
  const { allowCreate } = props

  const [total, setTotal] = useState(0)

  const {
    duplicateModeOn,
    fetchBounties,
    setClaimingBounty,
    setDefaultCreateFormValues,
    setIsCreateDialogOpen,
    apostleBountyFilterInput,
    gridRef,
    setPageNumberQuery,
    queryPageNumber
  } = useBountyContext()

  const { t } = useTranslation()

  const { account, requireAuth } = useAccountContext()

  const handleDuplicateClick = useCallback(
    (bounty: BountyNode) => {
      requireAuth(() => {
        setIsCreateDialogOpen(true)

        if (bounty) {
          setDefaultCreateFormValues({
            classId: bounty.bountyRequired.classID,
            minIV: bounty.bountyRequired.minIV,
            mainSkillId: bounty.bountyRequired.mainSkillFullID,
            otherSkillId1: bounty.bountyRequired.otherSkillFullID1,
            otherSkillId2: bounty.bountyRequired.otherSkillFullID2,
            mainSkillMinLevel: bounty.bountyRequired.minMainSkillLevel,
            otherSkillMinLevel1: bounty.bountyRequired.minOtherSkill1Level,
            otherSkillMinLevel2: bounty.bountyRequired.minOtherSkill2Level,
            quantity: bounty.initBountyNumber,
            price: formatEther(BigInt(bounty.unitPrice)),
            currency: bounty.currencyType
          })
        }
      })
    },
    [requireAuth, setDefaultCreateFormValues, setIsCreateDialogOpen]
  )

  const handleOnClaim = useCallback(
    (bounty: BountyNode) => {
      requireAuth(() => {
        setClaimingBounty(bounty)
      })
    },
    [requireAuth, setClaimingBounty]
  )

  const handleRenderActions = useCallback(
    (bounty: BountyNode | undefined) => {
      if (!bounty) {
        return
      }

      if (duplicateModeOn) {
        return (
          <DuplicateButton onClick={() => handleDuplicateClick(bounty)}>
            <DuplicateSVG fill={'#ffffff'} height={16} />
            {t('bounty.apostle.grid_action.duplicate')}
          </DuplicateButton>
        )
      }

      const disabled = bounty.creatorWalletAddress === account.walletAddress

      const disableReason = disabled
        ? t('bounty.apostle.grid_action.claim_disabled_owner')
        : ''

      return (
        <ClaimButton
          onClick={() => handleOnClaim(bounty)}
          disabled={disabled}
          disableReason={disableReason}
          className={bounty.bountyID.toString()}
        >
          {t('bounty.fulfill')}
        </ClaimButton>
      )
    },
    [
      duplicateModeOn,
      account.walletAddress,
      t,
      handleDuplicateClick,
      handleOnClaim
    ]
  )

  const handleFetchSuccess = useCallback<
    NonNullable<
      ComponentProps<typeof ApostleBountiesDataGrid>['onFetchSuccess']
    >
  >(
    (result, params) => {
      setTotal(result?.pageInfo.totalCount || 0)
      setPageNumberQuery(params.page)
    },
    [setPageNumberQuery]
  )

  useEffect(() => {
    gridRef.current?.refreshData()
  }, [apostleBountyFilterInput, gridRef])

  return (
    <Container>
      <BountyHeader allowCreate={allowCreate} totalCount={total} />
      <ApostleBountiesDataGrid
        gridRef={gridRef}
        fetchBounties={fetchBounties}
        renderActions={handleRenderActions}
        onFetchSuccess={handleFetchSuccess}
        defaultPage={queryPageNumber}
      />
    </Container>
  )
}

const ApostleBountyFilterPublic: FC = () => {
  const { apostleBountyFilterInput, setApostleBountyFilter } =
    useBountyContext()

  return (
    <ApostleBountyFilter
      apostleBountyFilterInput={apostleBountyFilterInput}
      setApostleBountyFilterInput={setApostleBountyFilter}
    />
  )
}

const ApostleBountiesRoot: FC<Props> = ({
  allowCreate = true,
  changeUrlOnQuery = true,
  fixedFilter,
  thirdNavBar
}) => {
  return (
    <BountyProvider
      changeUrlOnQuery={changeUrlOnQuery}
      fixedFilter={fixedFilter}
      nftType={NftType.Apostle}
    >
      <ListTemplate
        thirdNavBar={thirdNavBar}
        filter={R.isNil(fixedFilter) ? <ApostleBountyFilterPublic /> : null}
        sortDropDown={<></>}
        mainContent={<ApostleBountyContent allowCreate={allowCreate} />}
      />
      <CreateApostleBountyDialog />
      <ClaimApostleBountyDialog />
    </BountyProvider>
  )
}

type ContentProps = {
  allowCreate?: boolean
}

type Props = ContentProps & {
  changeUrlOnQuery?: boolean
  fixedFilter?: MayNull<BountyFilterInput>
  thirdNavBar: ReactNode
}

export default ApostleBountiesRoot
